import { httpsCallable } from 'firebase/functions';
import type { z } from 'zod';
import {
  bootstrapUserInput,
  completeDrillInput,
  createAnchorInput,
  deleteAccountInput,
  deleteAnchorInput,
  registerDeviceInput,
  rotateCalendarTokenInput,
  rotateRecoveryCodeInput,
  scanForForbiddenKeys,
  snoozeDrillInput,
  startDrillInput,
  updateAnchorScheduleInput,
  type AnchorStatus,
  type AnchorView,
  type BootstrapUserInput,
  type CompleteDrillInput,
  type CreateAnchorInput,
  type DeleteAnchorInput,
  type DrillView,
  type PhysicalVerificationType,
  type RegisterDeviceInput,
  type RotateRecoveryCodeInput,
  type SnoozeDrillInput,
  type StartDrillInput,
  type UpdateAnchorScheduleInput,
} from '@recovery/shared';
import { DEMO } from './devFlags';
import { firebaseFunctions } from './firebase';

type Ok = { ok: true };
type AnchorResult = { anchor: AnchorView };

export interface CompleteDrillResult extends AnchorResult {
  recordedVerification: PhysicalVerificationType;
  recoveryCodeMatched: boolean | null;
}

export interface SnoozeDrillResult {
  drill: DrillView;
  anchorStatus: AnchorStatus;
}

async function send<Input, Output>(
  name: string,
  schema: z.ZodType<Input>,
  input: unknown,
): Promise<Output> {
  const scan = scanForForbiddenKeys(input);
  if (!scan.ok) throw new Error(`Request rejected at field path: ${scan.path || '<root>'}.`);

  const parsed = schema.safeParse(input);
  if (!parsed.success) throw new Error('Request does not match the server contract.');

  if (DEMO) {
    const { demoSend } = await import('./demo');
    return demoSend(name, parsed.data) as Promise<Output>;
  }

  const callable = httpsCallable<Input, Output>(firebaseFunctions(), name);
  return (await callable(parsed.data)).data;
}

export function bootstrapUser(timezone: string, locale: string) {
  const input: BootstrapUserInput = { timezone, locale };
  return send<BootstrapUserInput, BootstrapUserInput & { anchorCount: number }>(
    'bootstrapUser',
    bootstrapUserInput,
    input,
  );
}

export function createAnchor(input: CreateAnchorInput): Promise<AnchorResult> {
  return send('createAnchor', createAnchorInput, input);
}

export function startDrill(input: StartDrillInput): Promise<{ drill: DrillView }> {
  return send('startDrill', startDrillInput, input);
}

export function completeDrill(input: CompleteDrillInput): Promise<CompleteDrillResult> {
  return send('completeDrill', completeDrillInput, input);
}

export function snoozeDrill(input: SnoozeDrillInput): Promise<SnoozeDrillResult> {
  return send('snoozeDrill', snoozeDrillInput, input);
}

export function registerDevice(input: RegisterDeviceInput): Promise<Ok> {
  return send('registerDevice', registerDeviceInput, input);
}

export function updateAnchorSchedule(input: UpdateAnchorScheduleInput): Promise<AnchorResult> {
  return send('updateAnchorSchedule', updateAnchorScheduleInput, input);
}

export function rotateRecoveryCode(input: RotateRecoveryCodeInput): Promise<AnchorResult> {
  return send('rotateRecoveryCode', rotateRecoveryCodeInput, input);
}

export function rotateCalendarToken(): Promise<{ url: string }> {
  return send('rotateCalendarToken', rotateCalendarTokenInput, {});
}

export function deleteAnchor(anchorId: DeleteAnchorInput['anchorId']): Promise<Ok> {
  return send('deleteAnchor', deleteAnchorInput, { anchorId });
}

export function deleteAccount(): Promise<Ok> {
  return send('deleteAccount', deleteAccountInput, { confirm: 'DELETE' });
}
